// ✅ Phase 5: DemoChat — the in-browser live test (Magic Demo)
// The customer watches their agent work RIGHT HERE in the browser, before any
// WhatsApp is connected. The demo runs through the REAL engine via the
// simulation test-chat API (mode='customer') — no hardcode, real answers.
import React, { useState, useRef, useEffect } from 'react';

interface ChatMsg {
  id: number;
  role: 'customer' | 'luna' | 'system';
  text: string;
  ts: number;
}

const DEMO_SCRIPT = [
  'හායි! මොනවාද ඔයාලා ළඟ තියෙන්නේ?',
  'එකක මිල කීයද? photos පෙන්නන්න පුළුවන්ද?',
  'හරි. delivery කොහොමද? order කරන්න ඕන නම් මොකද කරන්නේ?',
];

function authHeaders(token?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : null);
  if (t) h['Authorization'] = t.startsWith('Bearer ') ? t : `Bearer ${t}`;
  return h;
}

export default function DemoChat({ token, onDone, autoRun = true }: { token?: string; onDone?: () => void; autoRun?: boolean }) {
  const [messages, setMessages] = useState<ChatMsg[]>([
    { id: 0, role: 'system', text: '🎬 Live Test — ඔයාගේ agent මෙතැනදීම test කරන්න. "Magic Demo" එබූ විට demo customer කෙනෙක් අහන දේවල් ඔයාගේ agent උත්තර දෙනවා — මේක REAL engine එකයි!', ts: Date.now() },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [demoRunning, setDemoRunning] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(1);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const push = (role: ChatMsg['role'], text: string) => {
    setMessages((m) => [...m, { id: idRef.current++, role, text, ts: Date.now() }]);
  };

  const askEngine = async (question: string): Promise<string> => {
    try {
      const r = await fetch('/api/tenet/whatsapp/test-chat', {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ message: question, mode: 'customer' }),
      });
      const j = await r.json();
      return String(j?.reply || j?.replyText || j?.assistantMessage || j?.message || '').trim() || '(reply එකක් ආවේ නෑ)';
    } catch {
      return '⚠️ connection error — නැවත උත්සාහ කරන්න.';
    }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    push('customer', q);
    setBusy(true);
    const reply = await askEngine(q);
    push('luna', reply);
    setBusy(false);
  };

  const runMagicDemo = async () => {
    if (demoRunning || busy) return;
    setDemoRunning(true);
    push('system', '✨ Magic Demo — 🧠 main brain එකෙන්ම run වෙනවා...');
    // Speed: all demo questions hit the REAL engine in parallel, then the replies
    // are shown in order (3x faster than sequential waiting).
    const replies = await Promise.all(DEMO_SCRIPT.map((q) => askEngine(q)));
    for (let i = 0; i < DEMO_SCRIPT.length; i++) {
      push('customer', `🎬 Demo ${i + 1}/${DEMO_SCRIPT.length}: ${DEMO_SCRIPT[i]}`);
      push('luna', replies[i]);
    }
    push('system', '✨ Demo එක ඉවරයි! පහළින් ඔයාටම chat කරලා test කරන්නත් පුළුවන්.');
    setDemoRunning(false);
    if (onDone) onDone();
  };

  // ✅ Auto-run: the demo starts by itself when the panel appears (no clicks needed)
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    autoRanRef.current = true;
    if (autoRun) {
      const t = setTimeout(() => { runMagicDemo(); }, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="liquid-card-3d" style={{ display: 'flex', flexDirection: 'column', height: 480, overflow: 'hidden' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid rgba(125,211,252,0.18)' }}>
        <div style={{ width: 38, height: 38, borderRadius: 12, background: 'linear-gradient(140deg,#38BDF8,#2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, boxShadow: '0 6px 18px rgba(56,189,248,0.4)' }}>🤖</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#E6F4FF' }}>Luna — Live Test</div>
          <div style={{ fontSize: 11, color: '#A9C7E8' }}>{busy ? 'typing…' : '🧠 main brain · real engine'}</div>
        </div>
        <button className="lg-btn lg-btn-primary" style={{ padding: '8px 14px', fontSize: 12 }} onClick={runMagicDemo} disabled={demoRunning || busy}>
          {demoRunning ? '🎬 Running…' : '✨ Magic Demo'}
        </button>
      </div>

      {/* messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m) => {
          if (m.role === 'system') {
            return (
              <div key={m.id} style={{ alignSelf: 'center', maxWidth: '86%', textAlign: 'center', fontSize: 11.5, color: '#7DD3FC', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(125,211,252,0.22)', borderRadius: 12, padding: '8px 12px' }}>{m.text}</div>
            );
          }
          const isCustomer = m.role === 'customer';
          return (
            <div key={m.id} style={{
              alignSelf: isCustomer ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: 16,
              fontSize: 13,
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              color: isCustomer ? '#0A1628' : '#E6F4FF',
              background: isCustomer
                ? 'linear-gradient(140deg,#7DD3FC,#38BDF8)'
                : 'linear-gradient(163deg, rgba(22,46,78,0.9), rgba(10,22,40,0.92))',
              border: isCustomer ? 'none' : '1px solid rgba(125,211,252,0.28)',
              boxShadow: '0 6px 18px rgba(2,10,28,0.3)',
            }}>{m.text}</div>
          );
        })}
        {busy && (
          <div style={{ alignSelf: 'flex-start', padding: '10px 16px', borderRadius: 16, background: 'rgba(22,46,78,0.8)', border: '1px solid rgba(125,211,252,0.25)', fontSize: 13, color: '#7DD3FC' }}>
            <span className="animate-pulse">● ● ●</span>
          </div>
        )}
      </div>

      {/* input */}
      <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid rgba(125,211,252,0.18)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="ඔයාගේ agent එකට message එකක් ලියන්න…"
          style={{ flex: 1, padding: '11px 14px', borderRadius: 14, border: '1px solid rgba(125,211,252,0.28)', background: 'rgba(10,22,40,0.6)', color: '#E6F4FF', fontSize: 13, outline: 'none' }}
        />
        <button className="lg-btn lg-btn-primary" style={{ padding: '10px 18px' }} onClick={send} disabled={busy || !input.trim()}>➤</button>
      </div>
    </div>
  );
}
